import {
  CompanySelectDto,
  InviteCreateDto,
  InviteNestedSelectDto,
  InviteNestedSelectDtoStatusEnum,
  InviteRoleUpdateDtoRoleEnum,
  UserCompanyPatchDtoRoleEnum,
  UserNestedSelectFromCompanyDto,
} from '@@types/api.types';
import { IconButton } from '@components/core/icon-button.component';
import { Modal } from '@components/core/modal.component';
import { TeamMemberComponent } from '@components/shared/team-member.component';
import { Tile } from '@components/shared/tile.component';
import { useGetCompanyUsers } from '@queries/company.query';
import { useDeleteInvite, useSubmitInvites, useUpdateInvite } from '@queries/invite.query';
import { useDeleteUserCompany, useUpdateUserCompany } from '@queries/user-companies.query';
import { useGetCurrentUser } from '@queries/users.query';
import { EditIcon } from 'lucide-react';
import { useState } from 'react';
import { TeamStepComponent } from './company-company-profile-modal-team-step.component';

type CompanyManageTeamPageProps = {
  company: CompanySelectDto;
};

export const CompanyTeamDetailsTile: React.FC<CompanyManageTeamPageProps> = (props) => {
  const { company } = props;

  const { isPending: isUserPending } = useGetCurrentUser();

  const invites =
    company.invites.filter(
      (invite) =>
        invite.status === InviteNestedSelectDtoStatusEnum.PENDING &&
        new Date(invite.expiredAt).getTime() > new Date().getTime(),
    ) ?? [];

  const { data: companyUsers, isLoading: isCompanyUsersPending } = useGetCompanyUsers();

  const { mutateAsync: deleteInvite } = useDeleteInvite();
  const { mutateAsync: submitInvites } = useSubmitInvites();
  const { mutateAsync: updateInvite } = useUpdateInvite();
  const { mutateAsync: deleteUserCompany } = useDeleteUserCompany();
  const { mutateAsync: updateUserCompany } = useUpdateUserCompany();

  const handleInviteAdd = async (invite: InviteCreateDto) => {
    await submitInvites([invite]);
  };

  const handleInviteChangeRole = async (invite: InviteNestedSelectDto, role: InviteRoleUpdateDtoRoleEnum) => {
    await updateInvite({ data: { role }, id: invite.id });
  };

  const handleInviteRemove = async (invite: InviteNestedSelectDto) => {
    await deleteInvite(invite.id);
  };

  const handleUserChangeRole = async (user: UserNestedSelectFromCompanyDto, role: UserCompanyPatchDtoRoleEnum) => {
    await updateUserCompany({ data: { role }, id: user.userCompany!.id });
  };

  const handleUserRemove = async (user: UserNestedSelectFromCompanyDto) => {
    await deleteUserCompany({ id: user.userCompany!.id });
  };

  const [isManageTeamModalOpen, setIsManageTeamModalOpen] = useState(false);

  return (
    <Tile
      className="min-h-[142px]"
      emptyState={{
        title: 'Company Missing',
      }}
      hoverReveal
      isEmpty={!companyUsers?.users?.length && !invites.length}
      isLoading={isUserPending || isCompanyUsersPending}
    >
      <div className="flex flex-col gap-4">
        <div className="flex flex-row justify-between items-center w-full flex-wrap">
          <div className="text-[16px] font-medium text-[#1F1C1AE5]">Team</div>

          <div className="">
            <IconButton.HoverReveal
              ariaLabel="Manage Team"
              icon={<EditIcon />}
              onClick={() => {
                setIsManageTeamModalOpen(true);
              }}
              variant="secondary"
            />
          </div>
        </div>
        <div className="@container w-full">
          <div className="grid w-full grid-cols-1 gap-2 rounded-lg bg-gray-100 p-2 @[40rem]:grid-cols-2">
            {companyUsers?.users.map((user) => (
              <TeamMemberComponent
                key={user.id}
                user={user}
              />
            ))}
            {invites?.map((invite) => (
              <TeamMemberComponent
                key={invite.id}
                invite={invite}
              />
            ))}
          </div>
        </div>
        <Modal
          centered
          header={{ title: 'Manage Team' }}
          isOpen={isManageTeamModalOpen}
          onClose={() => setIsManageTeamModalOpen(false)}
          width="600px"
        >
          <TeamStepComponent
            handleInviteAdd={handleInviteAdd}
            handleInviteChangeRole={handleInviteChangeRole}
            handleInviteRemove={handleInviteRemove}
            handleUserChangeRole={handleUserChangeRole}
            handleUserRemove={handleUserRemove}
            invites={invites ?? []}
            companyId={company?.id || ''}
            users={companyUsers?.users ?? []}
            closeModal={() => setIsManageTeamModalOpen(false)}
          />
        </Modal>
      </div>
    </Tile>
  );
};
